import { ReactElement, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppSkeleton from "src/pages/AppSkeleton";
import { useCurrentUser } from "src/SessionBoundary";
import isOrphan from "./utils/isOrphan";
import isPendingAssignment, {
  getRemainingGraceMs,
} from "./utils/isPendingAssignment";

/**
 * Route wrapper for the onboarding survey screens.
 *
 * The server-side auto-assign trigger writes `organizationId` + `cohortId`
 * asynchronously after the client creates the user doc. This gate handles
 * the three states a fresh signup can be in:
 *
 *   1. Pending — the server has not resolved geo assignment yet. Render
 *      AppSkeleton for as long as organizationAssignmentStatus is pending.
 *   2. Real orphan — the server explicitly wrote no_match. Send to
 *      /onboarding/waitlist.
 *   3. Assigned — has org + cohort. Render children (the survey).
 *
 * Two reactivity paths:
 *
 * - When the trigger writes org/cohort, `onSnapshot` in AuthContext fires,
 *   `useCurrentUser()` re-emits, this component re-renders → pending → false
 *   → orphan → false → children. No timer involved.
 *
 * - New true-orphan signups receive an explicit no_match write, which drives
 *   the snapshot transition to waitlist. The timer below exists only for
 *   legacy records created before organizationAssignmentStatus shipped.
 *
 * Only the survey-side routes need this; the /onboarding/waitlist route
 * itself is unguarded so an orphan can land there. Self-recovery for the
 * case where the trigger lands AFTER the user reached the waitlist (slow
 * cold-start, admin-manual assignment) lives on WaitlistScreen itself.
 */
export default function OnboardingWaitlistGate({
  children,
}: {
  children: ReactElement;
}) {
  const user = useCurrentUser();
  // Forces a re-render when the grace window expires for a true orphan
  // whose user doc never changes (no onSnapshot fire). The value itself
  // is unused — only the setter matters.
  const [, forceTick] = useState(0);

  useEffect(() => {
    const remainingMs = getRemainingGraceMs(user);
    if (remainingMs === 0) return undefined;
    // Pad by 100ms so the timer fires AFTER isPendingAssignment flips false.
    const timeoutId = setTimeout(
      () => forceTick((t) => t + 1),
      remainingMs + 100
    );
    return () => clearTimeout(timeoutId);
  }, [user]);

  if (isPendingAssignment(user)) {
    return <AppSkeleton />;
  }

  if (isOrphan(user)) {
    // A structurally inconsistent matched record should never be described as
    // "no program". Keep loading while the server finishes/repairs the write.
    if (user.organizationAssignmentStatus === "matched") {
      return <AppSkeleton />;
    }
    return <Navigate to="/onboarding/waitlist" replace />;
  }

  return children;
}
